import {
  Alert,
  Autocomplete,
  Box,
  Container,
  createFilterOptions,
  FormControl,
  FormHelperText,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { EventSuggestion } from "../../types/eventTypes";
import { LoadingButton } from "@mui/lab";
import { DbProvider } from "../../../services/db/DbProvider";

export default function EventMessages({
  events,
}: {
  events: EventSuggestion[];
}) {
  if (!events) {
    events = [];
  }
  const eventFilter = createFilterOptions<EventSuggestion>();
  const [selectedEvent, setSelectedEvent] = useState<EventSuggestion | null>(
    null
  );
  const [selectedExisting, setSelectedExisting] = useState<boolean>(false);
  const [eventDate, setEventDate] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const handleToastClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setToastOpen(false);
  };

  const AlertPopup = () => {
    return (
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
      >
        <Alert
          onClose={handleToastClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          This is a success message!
        </Alert>
      </Snackbar>
    );
  };

  const submitForm = async () => {
    if (!selectedEvent || !selectedEvent?.name) {
      return alert("select an event or add a suggestion");
    }
    const db = new DbProvider();
    let messageObj;
    if (selectedExisting) {
      messageObj = {
        event: selectedEvent.uid,
        eventName: selectedEvent.name,
        date: eventDate,
      };
    } else {
      messageObj = { eventName: selectedEvent.name, comment: eventDate };
    }
    if (!messageObj) {
      return alert("an error happened please try again");
    }
    setSubmitLoading(true);
    try {
      await db.addDocWithRandomId("eventMessages", messageObj);
      setSelectedEvent(null);
      setEventDate("");
      setSubmitLoading(false);
      return alert("thanks");
    } catch (e) {
      setSubmitLoading(false);
      return alert("error");
    }
  };

  return (
    <Container sx={{ maxWidth: 400 }}>
      <AlertPopup />
      <Typography my={2} fontSize={20} fontWeight="bold" variant="body1">
        Contact us
      </Typography>
      <Typography my={2} fontSize={14} variant="body2">
        If you have an event suggestion or would like to see an event at a
        different date let us know
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: 2,
          maxWidth: 300,
          alignItems: "center",
        }}
      >
        <FormControl required>
          <Autocomplete
            aria-required
            id="select-event"
            value={selectedEvent}
            onChange={(event, newValue) => {
              // Entered without selecting from dropdown
              setSelectedExisting(false);
              if (typeof newValue === "string") {
                setSelectedEvent({
                  name: newValue,
                });
              } else if (newValue && newValue.inputValue) {
                // Create a new value from the user input (from dropdown)
                setSelectedExisting(false);
                setSelectedEvent({
                  name: newValue.inputValue,
                });
              } else {
                // Selected existing
                setSelectedExisting(true);
                setSelectedEvent(newValue);
              }
            }}
            filterOptions={(options, params) => {
              const filtered = eventFilter(options, params);
              const { inputValue } = params;
              // Suggest the creation of a new value
              const isExisting = options.some(
                (option) => inputValue === option.name
              );
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  name: `+ "${inputValue}"`,
                });
              }
              return filtered;
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={events}
            getOptionLabel={(option) => {
              // Value selected with enter, right from the input
              if (typeof option === "string") {
                return option;
              }
              // Add "xxx" option created dynamically
              if (option.inputValue) {
                return option.inputValue;
              }
              // Regular option
              return option.name;
            }}
            renderOption={(props, option) => <li {...props}>{option.name}</li>}
            sx={{ width: 300 }}
            freeSolo
            renderInput={(params) => (
              <TextField
                required
                aria-required
                {...params}
                label="event name or description"
              />
            )}
          />
          <FormHelperText>select an event or give us an idea</FormHelperText>
        </FormControl>
        <FormControl>
          <TextField
            type="text"
            label="date or comment"
            onChange={(e) => setEventDate(e.target.value)}
            value={eventDate}
          />
          <FormHelperText>
            if you have a preferred date for the event or any other comments
            please let us know
          </FormHelperText>
        </FormControl>
        <LoadingButton
          onClick={submitForm}
          loading={submitLoading}
          sx={{ maxWidth: 200 }}
          variant="outlined"
        >
          Send
        </LoadingButton>
      </Box>
    </Container>
  );
}
